import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { UserContext } from "../../../context/UserContext";

import {
	CountryBrandApplication,
	defaultCountryBrandApplication,
} from "../../../domain/CountryBrandApplication";
import { CountryBrandApplicationRepository } from "../../../domain/CountryBrandApplicationRepository";
import { ToastUtility } from "@syncfusion/ej2-react-notifications";

export interface ICountryBrandApplicationContext {
	countryBrandApplication: CountryBrandApplication;
	currentStep: number;
	setCurrentStep: Function;
	previousStep: number;
	setPreviousStep: Function;
	add: Function;
	update: Function;
	formRef: React.MutableRefObject<HTMLFormElement | undefined>;
	user: User | null | undefined;
	isLoading: boolean;
}

const CountryBrandApplicationContext = createContext<ICountryBrandApplicationContext | null>(null);

function CountryBrandApplicationContextProvider({
	children,
	repository,
}: {
	children: React.ReactElement;
	repository: CountryBrandApplicationRepository;
}) {
	const { uuid } = useParams() as { uuid: string };
	const navigate = useNavigate();
	const [setShowLoader] = useOutletContext();
	const { user, setUser } = useContext(UserContext);
	const [isLoading, setIsLoading] = useState(true);
	const [countryBrandApplication, setCountryBrandApplication] = useState<CountryBrandApplication>(
		() => defaultCountryBrandApplication(uuid)
	);

	useEffect(() => {
		setUser(localStorage.getItem("userToken"));
	}, []);

	const [currentStep, setCurrentStep] = useState(0);
	const [previousStep, setPreviousStep] = useState(0);

	const formRef = useRef();

	useEffect(() => {
		if (uuid !== "new") {
			setShowLoader(true);
			repository
				.getByID(uuid)
				.then((data) => {
					if (data) {
						data.attachments.forEach((e) => {
							e.name = e.fileName;
							e.type = e.group;
							e.status = "READ";
							e.url = `${process.env.REACT_APP_API_SERVER_URL}/api/CountryBrandApplications/CountryBrandApplications/DownloadFile?id=${e.id}&applicationId=${data.id}`;
						});
						setCountryBrandApplication(data);
						if (!user) {
							setCurrentStep(data.lastStepCompleted + 1);
						}
					}
				})
				.catch((e) => {
					console.error(e);
					ToastUtility.show(
						"Hubo un problema con el proceso de su solicitud.<br>Intente nuevamente en un minuto.",
						"Error",
						8000
					);
				})
				.finally((e) => {
					setIsLoading(false);
					setShowLoader(false);
				});
		} else {
			setIsLoading(false);
			setShowLoader(false);
		}
	}, [repository, uuid]);

	function add({ data, nextStep }: { data: Partial<CountryBrandApplication>; nextStep: number }) {
		const countryBrandApplicationData = { ...countryBrandApplication, ...data };
		setShowLoader(true);
		repository
			.add(countryBrandApplicationData)
			.then(function (id) {
				const newCountryBrandApplicationData = structuredClone(countryBrandApplicationData);
				newCountryBrandApplicationData.uuid = id;
				setCountryBrandApplication({ ...newCountryBrandApplicationData, id });
				setCurrentStep(nextStep);
				navigate(`/${id}`);
				//location.href = `${location.origin}/${id}`
			})
			.catch(function (e) {
				console.error(e);
				ToastUtility.show(
					"Hubo un problema con el proceso de su solicitud.<br>Intente nuevamente en un minuto.",
					"Error",
					8000
				);
			})
			.finally((e) => {
				setShowLoader(false);
			});
	}

	function update({
		data,
		nextStep,
	}: {
		data: Partial<CountryBrandApplication>;
		nextStep: number;
	}) {
		if (countryBrandApplication.isCompleted || user) {
			setCurrentStep(nextStep);
		} else {
			setShowLoader(true);
			const lastStepCompleted =
				currentStep > countryBrandApplication.lastStepCompleted
					? currentStep
					: countryBrandApplication.lastStepCompleted;
			const id = countryBrandApplication.id;
			const uuid = countryBrandApplication.id;
			const dataForUpdate = {
				...countryBrandApplication,
				...data,
				id,
				lastStepCompleted,
				uuid,
			};
			repository
				.update(dataForUpdate)
				.then(function (result) {
					setCountryBrandApplication({ ...dataForUpdate, ...result });
					setCurrentStep(nextStep);
				})
				.catch(function (e) {
					console.error(e);
					ToastUtility.show(
						"Hubo un problema con el proceso de su solicitud.<br>Intente nuevamente en un minuto.",
						"Error",
						8000
					);
				})
				.finally((e) => {
					setShowLoader(false);
				});
		}
	}

	return (
		<CountryBrandApplicationContext.Provider
			value={{
				countryBrandApplication,
				currentStep,
				formRef,
				setCurrentStep,
				add,
				update,
				setPreviousStep,
				previousStep,
				user,
				isLoading,
			}}
		>
			{children}
		</CountryBrandApplicationContext.Provider>
	);
}

function useCountryBrandApplicationContext() {
	return useContext(CountryBrandApplicationContext);
}

export { CountryBrandApplicationContextProvider, useCountryBrandApplicationContext };
